// src/components/principal/WebsiteApiKey.jsx  (v4)

import React, { useEffect, useState } from "react";
import api from "../../services/api";

const WebsiteApiKey = ({
  websiteTechUsed,
  schoolId,
  principalName,
  onError,
  onApiKeyReady,
}) => {
  const [apiKey, setApiKey] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const copyText = (text) => {
    navigator.clipboard.writeText(text).catch(() => setStatus("Failed to copy."));
  };

  const copyPrefilledEmail = () => {
    const body = `Dear Website Administrator,

Our school is now using the 'GhrianGhraffy App' to enhance how we share school photos with parents.

Please update our school website by installing the "GhrianGhraffy API Photo Display" plugin from the main WordPress plugin directory (you can find it by searching for "GhrianGhraffy").

Once the plugin is installed and activated:

1. In WordPress, go to Settings → General.
2. Scroll down to the "GhrianGhraffy" section.
3. Paste the API Key below into the "GhrianGhraffy API Key" field.
4. Click the "Connect to GhrianGhraffy" button.
5. Confirm that the correct school name and School ID appear.
6. Scroll to the bottom of the page and click "Save Changes".

API Key: ${apiKey}

(If you need it, our School ID is: ${schoolId})

After this, a "Photos" page will be created for our website. 
If our site uses a custom menu or page builder, this page may be created as a draft so that you can add it to the menu manually.

Please let me know when our website has been updated.

Kind regards,
${principalName}`;

    copyText(body);
    setStatus("Email text copied to clipboard!");
    setTimeout(() => setStatus(""), 3000);
  };

  useEffect(() => {
    const run = async () => {
      // Not WordPress → clear key + let parent know there is no plugin key to show
      if (websiteTechUsed === "Your School Website is not built with WordPress") {
        setApiKey(null);
        setStatus("");
        if (onApiKeyReady) onApiKeyReady(false);
        return;
      }

      setLoading(true);
      try {
        const { data } = await api.get("/auth/api-key");
        setApiKey(data.api_key);
        setStatus("");
        if (onApiKeyReady) onApiKeyReady(true);
      } catch (e) {
        if (e.response?.status === 404) {
          // No key yet → create one
          try {
            const res = await api.post("/auth/generate-api-key", {});
            setApiKey(res.data.api_key);
            setStatus("API key created successfully!");
            if (onApiKeyReady) onApiKeyReady(true);
          } catch (gerr) {
            console.error(gerr);
            setStatus(
              "Failed to generate API key. Please try again or contact support."
            );
            if (onApiKeyReady) onApiKeyReady(false);
          }
        } else if (e.response?.status === 400) {
          setStatus("No school associated with this user.");
          if (onApiKeyReady) onApiKeyReady(false);
        } else {
          setStatus("Failed to fetch API key. Please try again or contact support.");
          if (onApiKeyReady) onApiKeyReady(false);
        }
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [websiteTechUsed, onApiKeyReady]);

  if (websiteTechUsed === "Your School Website is not built with WordPress") {
    return (
      <p className="no-wordpress-message">
        It appears that your school&apos;s website was not built using WordPress. You can{" "}
        <a href="/contact" className="template-download-link">
          contact us
        </a>{" "}
        for integration options.
      </p>
    );
  }

  if (loading) return <p>Loading API key...</p>;

  if (!apiKey) {
    // Safety: make sure parent isn't left thinking we have a key
    if (onApiKeyReady) onApiKeyReady(false);
    return <p>Unable to generate API key. Please try again or contact support.</p>;
  }

  return (
    <div className="api-key-content">
      {/* Status row / badge */}
      <div className="plugin-status-row">
        <div>
          <h3 className="panel-title">Plugin API Access</h3>
          <p className="api-key-helper-text">
            Your unique API key is ready to use with the GhrianGhraffy WordPress
            plugin.
          </p>
        </div>
        <span className="status-pill status-pill--ready">
          <span className="status-pill-dot" aria-hidden="true" />
          <span>API key ready for WordPress plugin</span>
        </span>
      </div>

      <p>
        Copy the wording below and send it to your school&apos;s website administrator
        or IT provider:
      </p>
      <textarea
        value={`Dear Website Administrator,

Our school is now using the 'GhrianGhraffy App' to enhance how we share school photos with parents.

Please update our school website by installing the "GhrianGhraffy API Photo Display" plugin from the main WordPress plugin directory (you can find it by searching for "GhrianGhraffy").

Once the plugin is installed and activated:

1. In WordPress, go to Settings → General.
2. Scroll down to the "GhrianGhraffy" section.
3. Paste the API Key below into the "GhrianGhraffy API Key" field.
4. Click the "Connect to GhrianGhraffy" button.
5. Confirm that the correct school name and School ID appear.
6. Scroll to the bottom of the page and click "Save Changes".

API Key: ${apiKey}

(If you need it, our School ID is: ${schoolId})

After this, a "Photos" page will be created for our website. 
If our site uses a custom menu or page builder, this page may be created as a draft so that you can add it to the menu manually.

Please let me know when our website has been updated.

Kind regards,
${principalName}`}
        readOnly
        style={{ width: "100%", height: 320, fontFamily: "monospace", marginTop: 10 }}
        onClick={(e) => e.target.select()}
      />
      <button
        onClick={copyPrefilledEmail}
        style={{
          marginTop: 10,
          padding: "8px 16px",
          backgroundColor: "#00FFAF",
          color: "#0e6ebe",
          border: "none",
          cursor: "pointer",
        }}
      >
        Copy Email
      </button>
      {status && <p>{status}</p>}
    </div>
  );
};

export default WebsiteApiKey;
